import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useRef, useState } from "react";
import { ApiError, errorText } from "../api/common";
import {
  useApplyImportDiff,
  useDiscardImportDiff,
  useImportPendingDiffs,
  useLoadImportPendingDiff,
  useUploadImportDiff,
} from "../api/queries";
import type {
  ImportDiffDto,
  ImportDiffTransactionDto,
  UpdatedTransactionDto,
} from "../api/types";
import { CenteredSpinner } from "./CenteredSpinner";
import { DateDisplay } from "./DateDisplay";
import { DateTimeDisplay } from "./DateTimeDisplay";

interface Props {
  open: boolean;
  onClose: () => void;
  importId: string;
  preloadedDiff?: ImportDiffDto | null;
  /**
   * Called after a diff is applied — distinct from `onClose`, which also fires
   * on discard and on "Later" (closing without either). The import-from-invoice
   * flow uses it to finalize the period only when the invoice's rows actually
   * landed.
   */
  onApplied?: () => void;
}

type ModalState =
  | { kind: "pending-list" }
  | { kind: "file-picker" }
  | { kind: "loading" }
  | { kind: "diff"; diff: ImportDiffDto }
  | { kind: "applying"; diff: ImportDiffDto }
  | { kind: "discarding"; diff: ImportDiffDto }
  /**
   * `retry` is the state the failed operation started from, so "Try Again"
   * resumes it: re-applying the diff that failed to apply, re-picking from the
   * pending list. Falling back to the file picker would ask for a file that the
   * user never had to supply in the first place.
   */
  | { kind: "error"; message: string; retry: ModalState }
  | { kind: "expired" }
  /**
   * The pre-generated diff no longer matches the world it was computed
   * against (importer updated, or transactions changed since). It cannot be
   * shown or applied — the only action is to discard it and re-run.
   */
  | { kind: "stale"; pendingId: string };

const isExpiredError = (e: unknown) =>
  e instanceof ApiError && e.data.type === "pendingImportFileExpired";

const isStaleError = (e: unknown) =>
  e instanceof ApiError && e.data.type === "pendingImportDiffStale";

const TransactionTable = ({
  transactions,
  color,
}: {
  transactions: ImportDiffTransactionDto[];
  color: "success" | "error" | "primary";
}) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Date</TableCell>
        <TableCell>Merchant</TableCell>
        <TableCell>Description</TableCell>
        <TableCell>Source</TableCell>
        <TableCell align="right">Amount</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {transactions.map((tx, i) => (
        <TableRow
          key={i}
          sx={{
            bgcolor: `${color}.light`,
            opacity: 0.8,
          }}
        >
          <TableCell>
            <DateDisplay date={tx.datePaid} />
          </TableCell>
          <TableCell>{tx.merchant}</TableCell>
          <TableCell>{tx.description}</TableCell>
          <TableCell>{tx.source}</TableCell>
          <TableCell align="right">{tx.amount.amount}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const formatRef = (v: string | null | undefined) => (v ? v : "—");

const changedFields = (
  tx: UpdatedTransactionDto,
): [string, string, string][] => {
  const changes: [string, string, string][] = [];
  if (tx.oldKid !== tx.newKid) {
    changes.push(["KID", formatRef(tx.oldKid), formatRef(tx.newKid)]);
  }
  if (tx.oldPayToAccount !== tx.newPayToAccount) {
    changes.push([
      "Pay To Account",
      formatRef(tx.oldPayToAccount),
      formatRef(tx.newPayToAccount),
    ]);
  }
  if (tx.oldReference !== tx.newReference) {
    changes.push([
      "Reference",
      formatRef(tx.oldReference),
      formatRef(tx.newReference),
    ]);
  }
  if ((tx.oldDescription ?? "") !== (tx.newDescription ?? "")) {
    changes.push([
      "Description",
      formatRef(tx.oldDescription),
      formatRef(tx.newDescription),
    ]);
  }
  return changes;
};

const UpdatedTable = ({ updated }: { updated: UpdatedTransactionDto[] }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Date</TableCell>
        <TableCell>Merchant</TableCell>
        <TableCell align="right">Amount</TableCell>
        <TableCell>Changes</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {updated.map((tx, i) => (
        <TableRow key={i} sx={{ bgcolor: "warning.light", opacity: 0.8 }}>
          <TableCell>
            <DateDisplay date={tx.datePaid} />
          </TableCell>
          <TableCell>{tx.merchant}</TableCell>
          <TableCell align="right">{tx.amount.amount}</TableCell>
          <TableCell>
            {changedFields(tx).map(([label, from, to]) => (
              <Typography key={label} variant="body2">
                {label}: {from} → {to}
              </Typography>
            ))}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/** `before` alone when the two agree, `before → after` when they differ. */
const Change = ({ before, after }: { before: string; after: string }) =>
  before === after ? (
    <>{before}</>
  ) : (
    <>
      {before} →{" "}
      <Box component="span" sx={{ fontWeight: "bold" }}>
        {after}
      </Box>
    </>
  );

/**
 * Rows matched to a transaction that already exists, shown as before → after.
 * Both kinds of match rewrite the stored row in place rather than replacing it,
 * so what matters on screen is which of its values the file changes — above all
 * the amount, which a settled card purchase carries the currency markup and any
 * tip on top of.
 */
const PairTable = ({
  pairs,
  color,
  beforeLabel,
  afterLabel,
}: {
  pairs: {
    before: ImportDiffTransactionDto;
    after: ImportDiffTransactionDto;
  }[];
  color: "info" | "secondary";
  beforeLabel: string;
  afterLabel: string;
}) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>{beforeLabel} Date</TableCell>
        <TableCell>{afterLabel} Date</TableCell>
        <TableCell>Merchant</TableCell>
        <TableCell>Source</TableCell>
        <TableCell align="right">Amount</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {pairs.map(({ before, after }, i) => (
        <TableRow key={i} sx={{ bgcolor: `${color}.light`, opacity: 0.8 }}>
          <TableCell>
            <DateDisplay date={before.datePaid} />
          </TableCell>
          <TableCell>
            <DateDisplay date={after.datePaid} />
          </TableCell>
          <TableCell>{before.merchant}</TableCell>
          <TableCell>{before.source}</TableCell>
          <TableCell align="right">
            <Change before={before.amount.amount} after={after.amount.amount} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const CollapsibleSection = ({
  label,
  count,
  color,
  children,
}: {
  label: string;
  count: number;
  color: "success" | "error" | "info" | "warning" | "secondary" | "primary";
  children: React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <Box
      sx={{
        mb: 2,
      }}
    >
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Chip label={count} color={color} size="small" />
        <Typography variant="subtitle2">{label}</Typography>
        {expanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>
      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 1,
            overflowX: "auto",
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export const ImportDiffModal = ({
  open,
  onClose,
  importId,
  preloadedDiff,
  onApplied,
}: Props) => {
  const { data: pendingDiffs } = useImportPendingDiffs(importId, open);
  const uploadDiff = useUploadImportDiff(importId);
  const loadPendingDiff = useLoadImportPendingDiff(importId);
  const applyDiff = useApplyImportDiff(importId);
  const discardDiff = useDiscardImportDiff(importId);

  const initialState = (): ModalState => {
    if (preloadedDiff) return { kind: "diff", diff: preloadedDiff };
    if (pendingDiffs && pendingDiffs.length > 0)
      return { kind: "pending-list" };
    return { kind: "file-picker" };
  };

  // Which pending diffs exist, by content rather than by array identity: the
  // query hands back a fresh array on every refetch, and resetting the modal on
  // that would throw away the screen the user is on (and re-render forever if
  // the array is new each render).
  const pendingKey = (pendingDiffs ?? []).map((p) => p.pendingId).join(",");

  const [state, setState] = useState<ModalState>(initialState);
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevImportId, setPrevImportId] = useState(importId);
  const [prevPreloaded, setPrevPreloaded] = useState(preloadedDiff);
  const [prevPendingKey, setPrevPendingKey] = useState(pendingKey);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  // A pending-set change only resets the modal when the user is still on a
  // list/picker screen. Mid-review it must not: uploading a new version *adds*
  // a pending entry, and resetting on that refetch would yank the user off the
  // diff they just computed and back to the pending list.
  const reviewing =
    state.kind === "diff" ||
    state.kind === "applying" ||
    state.kind === "discarding" ||
    state.kind === "error" ||
    state.kind === "stale";

  if (pendingKey !== prevPendingKey) {
    setPrevPendingKey(pendingKey);
    if (!reviewing) {
      setState(initialState());
      setFile(null);
    }
  }

  if (
    open !== prevOpen ||
    importId !== prevImportId ||
    preloadedDiff !== prevPreloaded
  ) {
    setPrevOpen(open);
    setPrevImportId(importId);
    setPrevPreloaded(preloadedDiff);
    setState(initialState());
    setFile(null);
  }

  const handleUpload = () => {
    if (!file) return;
    setState({ kind: "loading" });
    uploadDiff.mutate(file, {
      onSuccess: (diff) => setState({ kind: "diff", diff }),
      onError: (e) =>
        setState({
          kind: "error",
          message: `Upload failed: ${errorText(e)}`,
          retry: { kind: "file-picker" },
        }),
    });
  };

  const handleLoadPending = (pendingId: string) => {
    const from = state;
    setState({ kind: "loading" });
    loadPendingDiff.mutate(pendingId, {
      onSuccess: (diff) => setState({ kind: "diff", diff }),
      onError: (e) => {
        if (isStaleError(e)) {
          setState({ kind: "stale", pendingId });
        } else if (isExpiredError(e)) {
          setState({ kind: "expired" });
        } else {
          setState({
            kind: "error",
            message: `Failed to load diff: ${errorText(e)}`,
            retry: from,
          });
        }
      },
    });
  };

  const handleApply = (diff: ImportDiffDto) => {
    setState({ kind: "applying", diff });
    applyDiff.mutate(diff.pendingId, {
      onSuccess: () => {
        onApplied?.();
        onClose();
      },
      onError: (e) => {
        if (isStaleError(e)) {
          setState({ kind: "stale", pendingId: diff.pendingId });
        } else if (isExpiredError(e)) {
          setState({ kind: "expired" });
        } else {
          setState({
            kind: "error",
            message: `Apply failed: ${errorText(e)}`,
            retry: { kind: "diff", diff },
          });
        }
      },
    });
  };

  const handleDiscardStale = (pendingId: string) => {
    setState({ kind: "loading" });
    discardDiff.mutate(pendingId, {
      onSuccess: () => onClose(),
      onError: (e) =>
        setState({
          kind: "error",
          message: `Discard failed: ${errorText(e)}`,
          retry: { kind: "stale", pendingId },
        }),
    });
  };

  const handleDiscard = (diff: ImportDiffDto) => {
    setState({ kind: "discarding", diff });
    discardDiff.mutate(diff.pendingId, {
      onSuccess: () => onClose(),
      onError: (e) =>
        setState({
          kind: "error",
          message: `Discard failed: ${errorText(e)}`,
          retry: { kind: "diff", diff },
        }),
    });
  };

  const renderContent = () => {
    switch (state.kind) {
      case "pending-list":
        return (
          <Box>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                mb: 2,
              }}
            >
              {pendingDiffs!.length === 1
                ? "There is 1 pending update."
                : `There are ${pendingDiffs!.length} pending updates.`}{" "}
              Select one to review, or upload a new version.
            </Typography>
            <List disablePadding>
              {pendingDiffs!.map((p) => (
                <ListItem
                  key={p.pendingId}
                  divider
                  secondaryAction={
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => handleLoadPending(p.pendingId)}
                      >
                        Review
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => {
                          discardDiff.mutate(p.pendingId, {
                            onSuccess: () => onClose(),
                          });
                        }}
                      >
                        Discard
                      </Button>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={<DateTimeDisplay date={p.createdAt} />}
                    secondary={p.pendingId}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case "file-picker":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              Upload a new version of this import file to preview the changes
              before applying.
            </Typography>
            <Button variant="outlined" component="label" fullWidth>
              {file ? file.name : "Select File"}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Button>
          </Box>
        );

      case "loading":
        return <CenteredSpinner />;

      case "diff":
      case "applying":
      case "discarding": {
        const diff = state.diff;
        // Rows the importer folded into their opposite leg. They leave storage
        // like a removal, but nothing is lost — the movement survives on the
        // outgoing row — so they get their own, calmer section.
        const merged = diff.merged ?? [];
        const mergedCount = merged.length;
        const isApplying =
          state.kind === "applying" || state.kind === "discarding";
        return (
          <Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Chip label={`${diff.added.length} added`} color="success" />
              <Chip label={`${diff.removed.length} removed`} color="error" />
              {mergedCount > 0 && (
                <Chip label={`${mergedCount} merged`} color="primary" />
              )}
              {diff.settling.length > 0 && (
                <Chip label={`${diff.settling.length} settling`} color="info" />
              )}
              {(diff.restated?.length ?? 0) > 0 && (
                <Chip
                  label={`${diff.restated!.length} restated`}
                  color="secondary"
                />
              )}
              {(diff.updated?.length ?? 0) > 0 && (
                <Chip
                  label={`${diff.updated!.length} updated`}
                  color="warning"
                />
              )}
              <Chip
                label={`${diff.unchangedCount} unchanged`}
                variant="outlined"
              />
            </Box>
            {diff.added.length > 0 && (
              <CollapsibleSection
                label="Added"
                count={diff.added.length}
                color="success"
              >
                <TransactionTable transactions={diff.added} color="success" />
              </CollapsibleSection>
            )}
            {diff.removed.length > 0 && (
              <CollapsibleSection
                label="Removed"
                count={diff.removed.length}
                color="error"
              >
                <TransactionTable transactions={diff.removed} color="error" />
              </CollapsibleSection>
            )}
            {mergedCount > 0 && (
              <CollapsibleSection
                label="Merged into their opposite leg"
                count={mergedCount}
                color="primary"
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  These are transfers between your own accounts, which the bank
                  export writes once from each side. Each row below is now
                  carried by its outgoing counterpart as a single transaction —
                  nothing is lost, and the transfer stops being counted twice.
                </Typography>
                <TransactionTable transactions={merged} color="primary" />
              </CollapsibleSection>
            )}
            {diff.settling.length > 0 && (
              <CollapsibleSection
                label="Settling (reserved → settled)"
                count={diff.settling.length}
                color="info"
              >
                <PairTable
                  pairs={diff.settling.map((p) => ({
                    before: p.reserved,
                    after: p.settled,
                  }))}
                  color="info"
                  beforeLabel="Reserved"
                  afterLabel="Settled"
                />
              </CollapsibleSection>
            )}
            {(diff.restated?.length ?? 0) > 0 && (
              <CollapsibleSection
                label="Restated (amount corrected in place)"
                count={diff.restated!.length}
                color="secondary"
              >
                <PairTable
                  pairs={diff.restated!.map((p) => ({
                    before: p.stored,
                    after: p.restated,
                  }))}
                  color="secondary"
                  beforeLabel="Stored"
                  afterLabel="Statement"
                />
              </CollapsibleSection>
            )}
            {(diff.updated?.length ?? 0) > 0 && (
              <CollapsibleSection
                label="Updated (fields refreshed from the statement)"
                count={diff.updated!.length}
                color="warning"
              >
                <UpdatedTable updated={diff.updated!} />
              </CollapsibleSection>
            )}
            {diff.added.length === 0 &&
              diff.removed.length === 0 &&
              mergedCount === 0 &&
              diff.settling.length === 0 &&
              (diff.restated?.length ?? 0) === 0 &&
              (diff.updated?.length ?? 0) === 0 && (
                <Alert severity="info">
                  No changes detected — the file is identical to the current
                  version. Apply it anyway to record the import as re-run with
                  the current importer.
                </Alert>
              )}
            {isApplying && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        );
      }

      case "error":
        return <Alert severity="error">{state.message}</Alert>;

      case "expired":
        return (
          <Alert severity="warning">
            This preview has expired — please upload the file again.
          </Alert>
        );

      case "stale":
        return (
          <Alert severity="warning">
            This preview is out of date — the importer or your transactions
            changed after it was generated. Discard it, then re-run the import
            to get a fresh preview.
          </Alert>
        );
    }
  };

  const renderActions = () => {
    switch (state.kind) {
      case "pending-list":
        return (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={() => setState({ kind: "file-picker" })}
              variant="outlined"
            >
              Upload New Version
            </Button>
          </>
        );

      case "file-picker":
        return (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleUpload} variant="contained" disabled={!file}>
              Preview Changes
            </Button>
          </>
        );

      case "loading":
        return (
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
        );

      case "diff": {
        const diff = state.diff;
        // Apply stays enabled even for an empty diff: applying is what stamps
        // the import with the current importer version, so a re-run that turns
        // out to change nothing still needs it to clear the needs-re-run flag.
        // Closing the diff calls nothing on the server: the pending update stays
        // stored (and listed on Home) until it is applied or discarded. "Later"
        // says so where "Cancel" read as throwing it away.
        return (
          <>
            <Tooltip describeChild title="Keep this update to review later">
              <Button onClick={onClose} color="inherit">
                Later
              </Button>
            </Tooltip>
            <Button
              onClick={() => handleDiscard(diff)}
              color="error"
              startIcon={<DeleteIcon />}
            >
              Discard
            </Button>
            <Button
              onClick={() => handleApply(diff)}
              variant="contained"
              color="primary"
            >
              Apply
            </Button>
          </>
        );
      }

      case "applying":
      case "discarding":
        return (
          <Button onClick={onClose} color="inherit" disabled>
            Later
          </Button>
        );

      case "error": {
        const retry = state.retry;
        return (
          <>
            <Button onClick={() => setState(retry)} color="inherit">
              Try Again
            </Button>
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
          </>
        );
      }

      case "expired":
        return (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={() => setState({ kind: "file-picker" })}
              variant="contained"
            >
              Upload Again
            </Button>
          </>
        );

      case "stale":
        return (
          <>
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
            <Button
              onClick={() => handleDiscardStale(state.pendingId)}
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
            >
              Discard
            </Button>
          </>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Upload New Version</DialogTitle>
      <DialogContent dividers>{renderContent()}</DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </Dialog>
  );
};
